import { ApolloError, useMutation } from "@apollo/client";
import { Spinner, Toast } from "@aurem/ui";
import ROUTES from "const/routes";
import { useAcceptInvitation } from "hooks/useAcceptInvitation";
import useAnalytics, { AnalyticsEvent } from "hooks/useAnalytics";
import { useNavigateAfterInvitation } from "hooks/useNavigateAfterInvitation";
import { AuthStatus, useAuth } from "providers/AuthProvider";
import { useI18n } from "providers/I18nProvider";
import { useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { gql } from "types";
import { GoogleOAuthState, parseOAuthState, useSignOut } from "utils/auth";

const LOGIN_WITH_GOOGLE = gql(/* GraphQL */ `
  mutation LoginUserWithGoogle($token: String!) {
    loginUserWithGoogleAuthToken(token: $token) {
      accessToken
      refreshToken
    }
  }
`);

const REGISTER_WITH_GOOGLE = gql(/* GraphQL */ `
  mutation RegisterUserWithGoogle($token: String!) {
    registerUserWithGoogleAuthToken(token: $token) {
      accessToken
      refreshToken
    }
  }
`);

const GoogleRedirect = () => {
  const { t } = useI18n();
  const { language } = useParams();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const { setAuthState } = useAuth();
  const { track } = useAnalytics();
  const { acceptInvitation } = useAcceptInvitation();
  const getDestination = useNavigateAfterInvitation();

  const [signInWithGoogle] = useMutation(LOGIN_WITH_GOOGLE);
  const [registerWithGoogle] = useMutation(REGISTER_WITH_GOOGLE);

  const isProcessing = useRef(false);

  const handleGoogleLogin = useCallback(
    async (token: string, oAuthState: GoogleOAuthState) => {
      if (isProcessing.current) return;
      isProcessing.current = true;

      try {
        await signOut();

        const { data } = await signInWithGoogle({ variables: { token } });
        const tokens = data?.loginUserWithGoogleAuthToken;
        if (!tokens?.accessToken || !tokens?.refreshToken) {
          throw new Error("Missing tokens");
        }

        setAuthState({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          status: AuthStatus.Authenticated,
        });
        track(AnalyticsEvent.SignInByGoogle);

        const invitationData = await acceptInvitation({
          accessToken: tokens.accessToken,
          invitationId: oAuthState.invitationId,
          type: oAuthState.invitationType,
        });

        const destination = invitationData
          ? getDestination({
              invitationData,
              invitationId: oAuthState.invitationId,
              type: oAuthState.invitationType,
            })
          : `/${language}`;

        void navigate(destination);
      } catch (err) {
        Toast.error({
          title:
            err instanceof ApolloError
              ? t("apps.admin.common.errorGeneric")
              : t("apps.admin.common.errorGeneric"),
        });
        void navigate(`/${oAuthState.language}/${ROUTES.SIGN_IN}`);
      } finally {
        isProcessing.current = false;
      }
    },
    [
      signOut,
      signInWithGoogle,
      setAuthState,
      track,
      acceptInvitation,
      getDestination,
      navigate,
      t,
      language,
    ],
  );

  const handleGoogleSignUp = useCallback(
    async (token: string, oAuthState: GoogleOAuthState) => {
      if (isProcessing.current) return;
      isProcessing.current = true;

      try {
        await signOut();

        const { data } = await registerWithGoogle({ variables: { token } });
        const tokens = data?.registerUserWithGoogleAuthToken;
        if (!tokens?.accessToken || !tokens?.refreshToken) {
          throw new Error("Missing tokens");
        }

        setAuthState({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          status: AuthStatus.Authenticated,
        });
        track(AnalyticsEvent.SignUpByGoogle);

        const invitationData = await acceptInvitation({
          accessToken: tokens.accessToken,
          invitationId: oAuthState.invitationId,
          type: oAuthState.invitationType,
        });

        const from = invitationData
          ? getDestination({
              invitationData,
              invitationId: oAuthState.invitationId,
              type: oAuthState.invitationType,
            })
          : undefined;

        void navigate(`/${language}/admin/${ROUTES.DISCLAIMERS}`, {
          state: { from },
        });
      } catch (err) {
        Toast.error({
          title:
            err instanceof ApolloError
              ? err.message || t("apps.admin.common.errorGeneric")
              : t("apps.admin.common.errorGeneric"),
        });
        void navigate(`/${oAuthState.language}/${ROUTES.SIGN_UP}`);
      } finally {
        isProcessing.current = false;
      }
    },
    [
      signOut,
      registerWithGoogle,
      setAuthState,
      track,
      acceptInvitation,
      getDestination,
      navigate,
      t,
      language,
    ],
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("code");
    if (!token) {
      Toast.error({ title: "Invalid token" });
      return;
    }

    const oAuthState = parseOAuthState(urlParams.get("state"));
    if (oAuthState instanceof Error) {
      Toast.error({ title: oAuthState.message });
      return;
    }

    if (isProcessing.current) return;

    if (oAuthState.action === "signin") {
      void handleGoogleLogin(token, oAuthState);
    } else if (oAuthState.action === "signup") {
      void handleGoogleSignUp(token, oAuthState);
    } else {
      Toast.error({ title: "Unknown OAuth action" });
    }
  }, [handleGoogleLogin, handleGoogleSignUp]);

  return (
    <div className="flex flex-grow items-center justify-center">
      <Spinner size="xl" text={t("apps.admin.common.tagline")} />
    </div>
  );
};

export default GoogleRedirect;
